import { useState, useEffect } from "react";
import {
  Box, Chip, FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { RunConversation } from "@common/domain/conversation-results";
import { flagRateByTurn, OUTCOME_LABEL, pct, runStats, runStopLabel, type ReviewStatus } from "./resultsLogic";
import { OutcomePip, Panel, ReviewCount, seriesColor, Swatch } from "./shared";

export interface TurnFilters {
  enabled: string[]; // sources shown
  attack: string; // "*" for all
  category: string; // "*" for all
}

export const filterRuns = (runs: RunConversation[], f: TurnFilters) =>
  runs.filter((r) => f.enabled.includes(r.source) && (f.attack === "*" || r.attack === f.attack) && (f.category === "*" || r.category === f.category));

interface Props {
  runs: RunConversation[];
  sources: string[];
  filters: TurnFilters;
  onFilters: (f: TurnFilters) => void;
  /** Reviewers per conversation, counting everyone (reviews themselves are blind). */
  reviewStatus: (conversationId: string) => ReviewStatus;
  selectedId: string | null;
  onOpen: (id: string) => void;
}

function FlagRateChart({ rs, sources }: { rs: RunConversation[]; sources: string[] }) {
  const theme = useTheme();
  const [hover, setHover] = useState<number | null>(null);
  if (!rs.length) return <Typography variant="body2" color="text.secondary">No runs match these filters.</Typography>;
  const maxR = Math.min(10, Math.max(1, ...rs.map((r) => r.rounds.length)));
  const series = sources.map((s) => ({ s, pts: flagRateByTurn(rs, s, maxR) }));
  const W = 520, H = 240, L = 40, R = 14, T = 12, B = 30;
  const x = (v: number) => L + (maxR === 1 ? (W - L - R) / 2 : ((v - 1) / (maxR - 1)) * (W - L - R));
  const y = (v: number) => T + (1 - v) * (H - T - B);
  const axis = { fill: theme.palette.text.secondary, fontSize: 11 };

  const onMove = (e: React.MouseEvent<SVGRectElement>) => {
    const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const sx = ((e.clientX - box.left) / box.width) * W;
    setHover(maxR === 1 ? 1 : Math.max(1, Math.min(maxR, Math.round(((sx - L) / (W - L - R)) * (maxR - 1) + 1))));
  };

  return (
    <Box sx={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Flag rate by turn number per source" style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke={t ? theme.palette.divider : theme.palette.text.disabled} />
            <text x={L - 6} y={y(t) + 4} textAnchor="end" style={axis}>{t * 100}%</text>
          </g>
        ))}
        {Array.from({ length: maxR }, (_, i) => <text key={i} x={x(i + 1)} y={H - 10} textAnchor="middle" style={axis}>T{i + 1}</text>)}
        {series.map(({ s, pts }) => {
          const p = pts.filter((d): d is NonNullable<typeof d> => !!d);
          const color = seriesColor(sources, s);
          return (
            <g key={s}>
              <polyline fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" points={p.map((d) => `${x(d.turn)},${y(d.rate)}`).join(" ")} />
              {p.map((d) => <circle key={d.turn} cx={x(d.turn)} cy={y(d.rate)} r={4} fill={color} stroke={theme.palette.background.default} strokeWidth={2} />)}
            </g>
          );
        })}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} stroke={theme.palette.text.disabled} />}
        <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="transparent" onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
      </svg>
      {hover != null && (
        <Box sx={{
          position: "absolute", top: 8, pointerEvents: "none", bgcolor: "rgba(26,26,26,.9)", color: "#fff", fontSize: "0.82rem", lineHeight: 1.5,
          px: 1.5, py: 0.75, borderRadius: 1, whiteSpace: "nowrap", zIndex: 2,
          ...(x(hover) > W / 2 ? { right: `${100 - (x(hover) / W) * 100 + 2}%` } : { left: `${(x(hover) / W) * 100 + 2}%` }),
        }}>
          <b>T{hover}</b>
          {series.map(({ s, pts }) => {
            const d = pts[hover - 1];
            return <div key={s}><span style={{ color: seriesColor(sources, s) }}>■</span> {s} {d ? `${pct(d.rate)} (${d.c}/${d.n})` : "–"}</div>;
          })}
        </Box>
      )}
    </Box>
  );
}

export default function TurnLog({ runs, sources, filters, onFilters, reviewStatus, selectedId, onOpen }: Props) {
  const theme = useTheme();
  const [cols, setCols] = useState<"category" | "attack">("category");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const rs = filterRuns(runs, filters);
  const paginatedRuns = rs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const srcs = sources.filter((s) => filters.enabled.includes(s));
  const rows = srcs.map((s) => [s, runStats(rs.filter((r) => r.source === s))] as const);
  const hasLat = rows.some(([, st]) => st.p50 != null);
  const hasStop = rs.some((r) => runStopLabel(r)); // only shown when the file records how runs ended
  const keys = [...new Set(rs.map((r) => r[cols]))].sort();
  const attacks = [...new Set(runs.map((r) => r.attack))];
  const categories = [...new Set(runs.map((r) => r.category))].sort();
  const toggle = (s: string) => onFilters({ ...filters, enabled: filters.enabled.includes(s) ? filters.enabled.filter((x) => x !== s) : [...filters.enabled, s] });
  const mono = { fontFamily: "monospace", fontSize: "0.8rem" };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 500, mb: 0.5 }}>Model / file</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {sources.map((s) => (
              <Chip key={s} label={<><Swatch color={seriesColor(sources, s)} />{s}</>} variant="outlined" aria-pressed={filters.enabled.includes(s)}
                onClick={() => toggle(s)} sx={{ opacity: filters.enabled.includes(s) ? 1 : 0.45 }} />
            ))}
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="turn-attack">Attack</InputLabel>
          <Select labelId="turn-attack" label="Attack" value={filters.attack} onChange={(e) => onFilters({ ...filters, attack: e.target.value })}>
            <MenuItem value="*">All</MenuItem>
            {attacks.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="turn-cat">Category</InputLabel>
          <Select labelId="turn-cat" label="Category" value={filters.category} onChange={(e) => onFilters({ ...filters, category: e.target.value })}>
            <MenuItem value="*">All</MenuItem>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Panel title="Summary" note="Flagged = turn classified harmful. Infra errors are left out of rates." flush>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell><TableCell align="right">Turn flag rate</TableCell><TableCell align="right">Flagged / scored</TableCell>
              <TableCell align="right">Runs flagged ≥1×</TableCell><TableCell align="right">Median first flag</TableCell><TableCell align="right">Infra errors</TableCell>
              {hasLat && <><TableCell align="right">Latency p50</TableCell><TableCell align="right">p95</TableCell></>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? rows.map(([s, st]) => (
              <TableRow key={s}>
                <TableCell sx={{ ...mono, whiteSpace: "nowrap" }}><Swatch color={seriesColor(sources, s)} />{s}</TableCell>
                <TableCell align="right" sx={mono}><b>{pct(st.rate)}</b></TableCell>
                <TableCell align="right" sx={mono}>{st.flagged} / {st.scored}</TableCell>
                <TableCell align="right" sx={mono}>{pct(st.runRate)} <Typography component="span" variant="caption" color="text.secondary">of {st.runs}</Typography></TableCell>
                <TableCell align="right" sx={mono}>{st.firstMed == null ? "–" : "T" + st.firstMed}</TableCell>
                <TableCell align="right" sx={mono}>{st.abort}</TableCell>
                {hasLat && <>
                  <TableCell align="right" sx={mono}>{st.p50 == null ? "–" : (st.p50 / 1000).toFixed(2) + " s"}</TableCell>
                  <TableCell align="right" sx={mono}>{st.p95 == null ? "–" : (st.p95 / 1000).toFixed(2) + " s"}</TableCell>
                </>}
              </TableRow>
            )) : <TableRow><TableCell colSpan={8}><Typography color="text.secondary">No sources selected.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 440px), 1fr))", gap: 2 }}>
        <Panel title="Turn flag rate" action={
          <ToggleButtonGroup size="small" exclusive value={cols} onChange={(_, v) => v && setCols(v)} aria-label="Columns">
            <ToggleButton value="category" sx={{ py: 0.25, textTransform: "none" }}>Category</ToggleButton>
            <ToggleButton value="attack" sx={{ py: 0.25, textTransform: "none" }}>Attack</ToggleButton>
          </ToggleButtonGroup>
        }>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ "& td, & th": { border: 0, p: "3px" } }}>
              <TableHead><TableRow><TableCell />{keys.map((k) => <TableCell key={k} align="center" sx={{ whiteSpace: "nowrap" }}>{k}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {srcs.map((s) => (
                  <TableRow key={s}>
                    <TableCell sx={{ ...mono, whiteSpace: "nowrap", pr: "12px !important" }}>{s}</TableCell>
                    {keys.map((k) => {
                      const st = runStats(rs.filter((r) => r.source === s && r[cols] === k));
                      if (st.rate == null) return <TableCell key={k} align="center"><Typography color="text.secondary">–</Typography></TableCell>;
                      return (
                        <TableCell key={k} align="center">
                          <Box title={`${s} · ${k}: ${st.flagged} of ${st.scored} turns flagged`} sx={{
                            minWidth: 64, px: 0.75, py: 1.1, borderRadius: 1, fontWeight: 500, lineHeight: 1.2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08 + st.rate * 0.87),
                            color: st.rate > 0.55 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                          }}>
                            {pct(st.rate)}<Box component="small" sx={{ display: "block", fontSize: "0.7rem", opacity: 0.8, fontWeight: 400 }}>n={st.scored}</Box>
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="text.secondary">Darker = higher flag rate · n = scored turns</Typography>
        </Panel>
        <Panel title="Flag rate by turn number" note="Does the classifier fall behind as the attack escalates?">
          <FlagRateChart rs={rs} sources={srcs} />
          {srcs.length > 1 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {srcs.map((s) => <Chip key={s} size="small" variant="outlined" label={<><Swatch color={seriesColor(sources, s)} />{s}</>} />)}
            </Box>
          )}
        </Panel>
      </Box>

      <Panel title="Runs" flush note={
        <Box component="span" sx={{ display: "inline-flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
          <OutcomePip outcome="flagged" /> {OUTCOME_LABEL.flagged.toLowerCase()} · <OutcomePip outcome="passed" /> {OUTCOME_LABEL.passed.toLowerCase()} · <OutcomePip outcome="abort" /> {OUTCOME_LABEL.abort.toLowerCase()} · Reviews: ✓ = reviewed by you
        </Box>
      }>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell><TableCell>Attack</TableCell><TableCell>Category</TableCell><TableCell>Turns</TableCell>
              <TableCell align="right">Flagged</TableCell>{hasStop && <TableCell>Run outcome</TableCell>}<TableCell align="right">Reviews</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRuns.length ? paginatedRuns.map((run) => {
              const st = runStats([run]), stop = runStopLabel(run);
              return (
                <TableRow
                  key={run.id} hover tabIndex={0} selected={selectedId === run.id} onClick={() => onOpen(run.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(run.id); } }}
                  sx={{ cursor: "pointer", "&.Mui-selected": { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                >
                  <TableCell sx={{ ...mono, whiteSpace: "nowrap" }}><Swatch color={seriesColor(sources, run.source)} />{run.source}</TableCell>
                  <TableCell sx={mono}>{run.attack}</TableCell>
                  <TableCell sx={mono}>{run.category}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: "3px", flexWrap: "wrap", maxWidth: 220 }}>
                      {run.rounds.map((r, k) => <span key={k} title={`Turn ${k + 1}: ${OUTCOME_LABEL[r.outcome]}${r.code ? ` (${r.code})` : ""}`}><OutcomePip outcome={r.outcome} /></span>)}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={mono}>{st.flagged} / {st.scored}</TableCell>
                  {hasStop && <TableCell>{stop && <Chip size="small" label={String(stop).slice(0, 80)} />}</TableCell>}
                  <TableCell align="right"><ReviewCount {...reviewStatus(run.id)} /></TableCell>
                </TableRow>
              );
            }) : <TableRow><TableCell colSpan={7}><Typography color="text.secondary">No runs match these filters.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={rs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Panel>
    </Box>
  );
}
